/**
 * The message stream, composer, and per-message action set for one chat,
 * replacing ISSUE-279's placeholder `detail` pane content (ISSUE-280 plan
 * WI-24, mirroring mobile WI-13's Decisions block for web idioms).
 *
 * Delete goes through the already-shipped `useDeleteMessage` (messageModeration,
 * ISSUE-271) instead of a second hand-rolled confirm + `/message/delete` call
 * this plan would otherwise write from scratch. It already has the own vs
 * moderator confirm copy and error handling this screen needs for the
 * own-message-only Delete row this plan scopes.
 */
import { useGifSelect } from "@sportsmet/media-sdk/private";
import {
  useCallSdk,
  useChatGet,
  useChatListMembers,
  useChatMarkRead,
  useMessageEdit,
  useMessageList,
  useMessagePin,
  useMessageSend,
  useMessageUnpin,
  usePollVote,
  useReactionSet,
} from "@sportsmet/messaging-sdk/private";
import { useBlockList } from "@sportsmet/profile-sdk/private";
import { Button, REDUCED_MOTION_SAFE } from "@sportsmet/ui";
import { useNavigate } from "@tanstack/react-router";
import type { DragEvent, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { useAuth } from "../../auth/AuthProvider";
import {
  extractErrorCode,
  queryApiErrorIdentity,
  showApiError,
  showOfflineError,
  showQueryApiError,
} from "../../errors/apiErrorBus";
import { useOverlay } from "../../routeState/useOverlay";
import { SectionEmptyState } from "../../shell/SectionScreen";
import { muteHeaderLabel } from "../chatDetails/muteDisplay";
import { PinnedBar } from "../chatDetails/PinnedBar";
import { BlockedMessageLine } from "../messageModeration/BlockedMessageLine";
import {
  BLOCKED_ROSTER_PAGE_LIMIT,
  isSenderBlockedByRoster,
  toBlockedRoster,
} from "../messageModeration/blockedRoster";
import {
  BLOCK_ACTION_LABEL,
  DELETE_ACTION_LABEL,
  REPORT_ACTION_LABEL,
} from "../messageModeration/moderationCopy";
import { canDeleteMessage } from "../messageModeration/moderationRules";
import { useBlockFan } from "../messageModeration/useBlockFan";
import { useDeleteMessage } from "../messageModeration/useDeleteMessage";
import {
  COMPOSER_DROP_PHOTOS_LABEL,
  PDF_FILE_NAME_FALLBACK,
} from "./chatExtrasCopy";
import { ChatAvatar } from "./ChatAvatar";
import { ChatMembersDialog } from "./ChatMembersDialog";
import { Composer, type typeComposerPdfSend } from "./Composer";
import { imageFilesFrom, isFileDrag } from "./composerEmojiLogic";
import {
  resolveConversationRows,
  type typeRenderRow,
} from "./conversationListState";
import { DayDivider } from "./DayDivider";
import {
  JUMP_BACK_WALK_MAX_PAGES,
  JUMP_BACK_WALK_PAGE_LIMIT,
  JUMP_NOT_FOUND_COPY,
  JUMP_NOTE_DISMISS_LABEL,
  JUMP_SEARCHING_COPY,
  JUMP_TOO_FAR_BACK_COPY,
  type typeJumpNote,
  type typeJumpWalkResult,
  walkBackToMessage,
} from "./jumpBackWalk";
import { resolveMarkReadStep, type typeMarkReadSent } from "./markReadGate";
import {
  appendOlderPage,
  catchUpMessages,
  hasCatchUpGap,
  mergeFreshPage,
  optimisticMessageId,
  type typeMessageGameCard,
  type typeMessagePoll,
  type typeMessageRow,
} from "./mergeMessagePages";
import type { typeActionMenuRow } from "./MessageActionDialog";
import { MessageActionDialog } from "./MessageActionDialog";
import { canEditMessage, resolveCopyText } from "./messageActionRules";
import { MessageBubble } from "./MessageBubble";
import { stripMentionMarkup } from "./messageText";
import { resolveReactionTap } from "./reactionLogic";
import { ReactionPickerDialog } from "./ReactionPickerDialog";
import {
  activeTimeoutIso,
  CHAT_SUSPENDED_COMPOSER_LINE,
  timedOutComposerLine,
  timeoutRefetchDelayMs,
} from "./memberTimeoutLogic";
import { ReactorsDialog } from "./ReactorsDialog";
import { SeasonDivider } from "./SeasonDivider";
import {
  CHAT_EMPTY_STATE_BODY,
  CHAT_MEMBERS_TITLE,
} from "./standaloneChatCopy";
import { SystemLine } from "./SystemLine";
import {
  LOAD_OLDER_THRESHOLD_PX,
  useConversationScroll,
} from "./useConversationScroll";

const ACTIVE_CHAT_REFRESH_SECONDS_FALLBACK = 5;

const JUMP_NOTE_COPY: Record<typeJumpNote, string> = {
  searching: JUMP_SEARCHING_COPY,
  tooFarBack: JUMP_TOO_FAR_BACK_COPY,
  notFound: JUMP_NOT_FOUND_COPY,
};

// Matches the literal sheet name `messages.tsx`'s own `CHAT_DETAILS_SHEET`
// constant opens (RF-2). Kept as its own local constant rather than an
// import from the route file, which would create a route to feature import
// cycle: `messages.tsx` already imports `ConversationPane` from here.
const CHAT_DETAILS_SHEET = "chat-details";

// Matches `messages.tsx`'s own `REPORT_DIALOG` constant, for the same
// route to feature import cycle reason as `CHAT_DETAILS_SHEET` above.
// `messages.tsx` already mounts `ReportMessageDialog` driven by
// `?dialog=report-message&messageId=&senderId=` (ISSUE-271 WI-W8); this
// pane only ever needs to open it, never to render it a second time.
const REPORT_DIALOG = "report-message";

/**
 * `announcementKind` arrives on `/message/list` as of ISSUE-749. Until then
 * this file mapped every item to null, which meant a `seasonStarted`
 * announcement rendered as a plain system line and the season-divider branch
 * in `conversationListState` could never fire against a real response.
 *
 * The mapper below now reads the wire value. An absent one is still null,
 * which the row resolver already treats as the safe default.
 */
// `pdf` is on this list even though nothing renders it yet (ISSUE-750). It
// was omitted while every other kind was here, so a PDF arrived, failed the
// membership test below and became a `text` row: the kind was destroyed at the
// door, and the bubble could not have branched on it if it wanted to. That is
// the ISSUE-749 mistake one field over, where a client branch written against
// a value the client never receives typechecks and tests green while being
// structurally unreachable. `apps/mobile/src/messages/mergeMessagePages.ts`
// already keeps the kind; this makes the two halves agree.
export const KNOWN_MESSAGE_KINDS = [
  "text",
  "image",
  "gif",
  "poll",
  "gameCard",
  "systemAnnouncement",
  "pdf",
] as const;

function toMessageRow(item: {
  messageId: string;
  senderId: string;
  senderDisplayName: string;
  senderAvatarUrl?: string | null;
  kind: string;
  body?: string | null;
  mediaObjectIds?: string[] | null;
  poll?: typeMessagePoll | null;
  gameCard?: typeMessageGameCard | null;
  // byteSize arrives optional on the wire (media-api's own get-batch omits
  // it for anything not published); reshaped to the always-present
  // `number | null` typeMessagePdf carries below.
  pdf?: { fileName: string; byteSize?: number | null } | null;
  gifObjectId?: string | null;
  replyToMessageId?: string | null;
  replyToPreview?: string | null;
  isEdited: boolean;
  isPinned: boolean;
  isDeleted: boolean;
  sentAtIso: string;
  editedAtIso?: string | null;
  announcementKind?: string | null;
  reactions: { emoji: string; count: number; reactedByMe: boolean }[];
  linkPreview?: {
    url: string;
    title: string;
    description?: string | null;
    siteName?: string | null;
    hostname: string;
  } | null;
}): typeMessageRow {
  const kind = (KNOWN_MESSAGE_KINDS as readonly string[]).includes(item.kind)
    ? (item.kind as typeMessageRow["kind"])
    : "text";
  return {
    messageId: item.messageId,
    senderId: item.senderId,
    senderDisplayName: item.senderDisplayName,
    senderAvatarUrl: item.senderAvatarUrl ?? null,
    kind,
    body: item.body ?? null,
    mediaObjectIds: item.mediaObjectIds ?? null,
    poll: item.poll ?? null,
    gameCard: item.gameCard ?? null,
    pdf: item.pdf
      ? { fileName: item.pdf.fileName, byteSize: item.pdf.byteSize ?? null }
      : null,
    gifObjectId: item.gifObjectId ?? null,
    replyToMessageId: item.replyToMessageId ?? null,
    replyToPreview: item.replyToPreview ?? null,
    isEdited: item.isEdited,
    isPinned: item.isPinned,
    isDeleted: item.isDeleted,
    sentAtIso: item.sentAtIso,
    editedAtIso: item.editedAtIso ?? null,
    announcementKind: item.announcementKind ?? null,
    reactions: item.reactions,
    // ISSUE-1360. Only an https card is ever drawn; the server only stores
    // one, and this keeps a bad row from becoming a link to anything else.
    linkPreview:
      item.linkPreview != null && item.linkPreview.url.startsWith("https://")
        ? {
            url: item.linkPreview.url,
            title: item.linkPreview.title,
            description: item.linkPreview.description ?? null,
            siteName: item.linkPreview.siteName ?? null,
            hostname: item.linkPreview.hostname,
          }
        : null,
  };
}

/**
 * Everything `submitPending` needs to retry a send: the one field each kind
 * this composer can send needs beyond `chatId`/`requestId`/`replyToMessageId`,
 * which every kind shares.
 */
type typePendingSendPayload =
  | {
      kind: "text";
      requestId: string;
      body: string;
      replyToMessageId: string | null;
    }
  | {
      kind: "image";
      requestId: string;
      mediaObjectIds: string[];
      body: string | null;
      replyToMessageId: string | null;
    }
  | {
      kind: "poll";
      requestId: string;
      pollDraft: {
        question: string;
        allowMultiple: boolean;
        optionLabels: string[];
      };
      replyToMessageId: string | null;
    }
  | {
      kind: "gif";
      requestId: string;
      /** Already a media-api object id, not a provider id: `/gif/select`
       *  ran first and re-hosted the bytes. A retry therefore re-sends the
       *  same object rather than paying the provider a second time. */
      gifObjectId: string;
      replyToMessageId: string | null;
    }
  | {
      /** ISSUE-1057 R4. One uploaded PDF; the pdf branch takes no caption. */
      kind: "pdf";
      requestId: string;
      mediaObjectId: string;
      replyToMessageId: string | null;
    };

function buildSendVariables(
  chatId: string,
  payload: typePendingSendPayload,
): Parameters<ReturnType<typeof useMessageSend>["mutateAsync"]>[0] {
  const base = {
    chatId,
    requestId: payload.requestId,
    replyToMessageId: payload.replyToMessageId,
  };
  if (payload.kind === "text") {
    return { ...base, kind: "text" as const, body: payload.body };
  }
  if (payload.kind === "image") {
    return {
      ...base,
      kind: "image" as const,
      body: payload.body,
      mediaObjectIds: payload.mediaObjectIds,
    };
  }
  if (payload.kind === "gif") {
    return {
      ...base,
      kind: "gif" as const,
      gifObjectId: payload.gifObjectId,
    };
  }
  if (payload.kind === "pdf") {
    return {
      ...base,
      kind: "pdf" as const,
      mediaObjectId: payload.mediaObjectId,
    };
  }
  return { ...base, kind: "poll" as const, pollDraft: payload.pollDraft };
}

/**
 * GLOSSARY-21 wording. Only a pool's chat has a Commissioner. A private chat
 * can never be announcements only (/chat/set-post-policy refuses it), so every
 * non-pool value gets the stand-alone wording.
 */
function announcementsOnlyLine(
  chatType: "pool" | "private" | "standalone" | undefined,
): string {
  if (chatType === "pool") {
    return "Only the Commissioner and Admins can post here.";
  }
  return "Only Admins can post here.";
}

/**
 * Only a pool's chat names the pool. A stand-alone chat and a private chat
 * have no pool, so every other chat type gets the stand-alone line.
 * ISSUE-1068.
 */
function chatOffLine(
  chatType: "pool" | "private" | "standalone" | undefined,
): string {
  if (chatType === "pool") {
    return "Chat is off for this pool.";
  }
  return "This chat is off.";
}

export type typeConversationPaneProps = {
  chatId: string;
  /**
   * Set from the route's `?messageId=` (RF-3). `ChatDetailsPanel` and this
   * pane are siblings, so a jump requested from its `Pins` tab travels
   * through the URL rather than a context, a ref or an event bus.
   */
  jumpToMessageId?: string | undefined;
  onLeftOrDeleted?: (() => void) | undefined;
  /**
   * SMOKE-1 (QA round 3). Below the two-pane width the open conversation
   * fills the screen and the chat list is hidden, so the header carries a
   * back arrow to the list. At the two-pane width the list sits beside the
   * pane and the arrow hides.
   */
  onBack?: (() => void) | undefined;
};

export const CONVERSATION_BACK_LABEL = "Back to Messages";

const ConversationBackGlyph = (): ReactNode => (
  <svg
    aria-hidden="true"
    focusable="false"
    viewBox="0 0 24 24"
    className="h-icon-md w-icon-md"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 5 8 12 15 19" />
  </svg>
);

export const ConversationPane = (
  props: typeConversationPaneProps,
): ReactNode => {
  const { chatId, onLeftOrDeleted } = props;
  const { state: authState } = useAuth();
  const viewerUserId =
    authState.status === "signedIn" ? authState.user.userId : "";
  const overlay = useOverlay();
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement>(null);
  const photoIntakeRef = useRef<((files: File[]) => void) | null>(null);
  const dragDepthRef = useRef(0);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [refreshMs, setRefreshMs] = useState(
    ACTIVE_CHAT_REFRESH_SECONDS_FALLBACK * 1000,
  );
  // Local component state is correct here rather than URL state: this dialog
  // is scoped to the pane, the route's single `dialog` slot is already taken
  // by the report dialog, and `overlay.ts`'s two slots are `sheet` and
  // `dialog` with `dialog` already spoken for on this route. Do not "fix"
  // this to URL state; there is nowhere left to put it.
  const [isMembersOpen, setIsMembersOpen] = useState(false);

  const chatGet = useChatGet({ chatId }, { enabled: Boolean(chatId) });
  // ISSUE-1364. Re-read the chat a second after a timeout ends, so canPost
  // catches up and the composer comes back without a reload.
  const rawTimedOutUntilIso = chatGet.data?.data?.timedOutUntilIso ?? null;
  const refetchChatGet = chatGet.refetch;
  useEffect(() => {
    const delay = timeoutRefetchDelayMs(rawTimedOutUntilIso, new Date());
    if (delay === null) return;
    const timer = setTimeout(() => {
      void refetchChatGet();
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [rawTimedOutUntilIso, refetchChatGet]);
  // ISSUE-1355. The header's picture, resolved by messaging-api: the other
  // fan's photo for a private chat, the chat's own for a stand-alone chat.
  const headerPhotoUrl = chatGet.data?.data?.avatarUrl ?? null;
  const liveQuery = useMessageList(
    { chatId },
    { enabled: Boolean(chatId), refetchInterval: refreshMs },
  );
  const liveQueryErrorCode = extractErrorCode(liveQuery.data);
  const markRead = useChatMarkRead();
  const lastMarkReadRef = useRef<typeMarkReadSent>(null);
  const chatMembers = useChatListMembers(
    { chatId },
    { enabled: Boolean(chatId) },
  );

  const [messages, setMessages] = useState<typeMessageRow[]>([]);
  const [olderCursor, setOlderCursor] = useState<string | null>(null);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isPrependingOlder, setIsPrependingOlder] = useState(false);
  // ISSUE-1054: the inline note under the pinned bar while a jump walks back
  // through older pages, or when it cannot reach the message.
  const [jumpNote, setJumpNote] = useState<typeJumpNote | null>(null);
  const pendingJumpMessageIdRef = useRef<string | null>(null);
  const jumpWalkTokenRef = useRef(0);
  // The token of the walk that currently owns `isLoadingOlder`. A walk a
  // newer one superseded must not clear the flag the newer walk still holds.
  const activeWalkTokenRef = useRef<number | null>(null);

  const [pendingSendState, setPendingSendState] = useState<
    Record<string, "sending" | "failed">
  >({});
  const [pendingSendPayload, setPendingSendPayload] = useState<
    Record<string, typePendingSendPayload>
  >({});

  const [replyTo, setReplyTo] = useState<{
    messageId: string;
    preview: string;
  } | null>(null);
  const [editingMessage, setEditingMessage] = useState<{
    messageId: string;
    body: string;
  } | null>(null);
  const [actionTarget, setActionTarget] = useState<Extract<
    typeRenderRow,
    { kind: "message" }
  > | null>(null);
  const [reactionTargetId, setReactionTargetId] = useState<string | null>(null);
  // ISSUE-1361. Whose reactions the "who reacted" dialog lists, and which
  // chip opened it. Local state for the same reason as `isMembersOpen`.
  const [reactorsTarget, setReactorsTarget] = useState<{
    messageId: string;
    emoji: string | null;
  } | null>(null);

  const rowCount = messages.length;
  const scroll = useConversationScroll({
    containerRef,
    rowCount,
    isPrependingOlder,
  });
  const isAtLatestBeforeUpdateRef = useRef(true);

  const rows = resolveConversationRows({
    messages,
    viewerUserId,
    nowMs: Date.now(),
  });
  const orderedRows = [...rows].reverse();

  const messageSend = useMessageSend();
  const gifSelect = useGifSelect();
  const messageEdit = useMessageEdit();
  const reactionSet = useReactionSet();
  const messagePin = useMessagePin();
  const messageUnpin = useMessageUnpin();
  const pollVote = usePollVote();
  const [votingMessageIds, setVotingMessageIds] = useState<
    Record<string, true>
  >({});
  const [failedVoteMessageIds, setFailedVoteMessageIds] = useState<
    Record<string, true>
  >({});
  const { deleteMessage } = useDeleteMessage();
  const { blockFan } = useBlockFan();
  const { callSdk } = useCallSdk();

  // Built once per pane, not per row (WI-W12's decision): the roster below
  // is read for every message rather than each row calling `useBlockList` on
  // its own. While the call is still pending, the roster is empty and
  // complete, which correctly renders every bubble bare rather than
  // collapsing messages from nobody in particular. A read that FAILED is a
  // different case: the roster below reads as incomplete, and the fail
  // closed rule shields every sender, because a truncated or unreadable
  // list used as a membership test returns the wrong answer, and the wrong
  // answer here shows a fan content from somebody they blocked.
  const blockList = useBlockList({ limit: BLOCKED_ROSTER_PAGE_LIMIT });
  const blockedRoster = toBlockedRoster({
    isPending: blockList.isPending,
    isError: blockList.isError,
    response: blockList.data,
  });

  useEffect(() => {
    const code = extractErrorCode(chatGet.data);
    if (code !== null) {
      showApiError(chatGet.data, undefined, chatGet.dataUpdatedAt);
    }
  }, [chatGet.dataUpdatedAt, chatId]);

  useEffect(() => {
    showQueryApiError(
      liveQuery.data,
      queryApiErrorIdentity("message/list", chatId),
      liveQueryErrorCode,
      liveQuery.dataUpdatedAt,
    );
  }, [liveQueryErrorCode, liveQuery.dataUpdatedAt, chatId]);

  useEffect(() => {
    const seconds = liveQuery.data?.data?.refreshAfterSeconds;
    if (typeof seconds === "number") setRefreshMs(seconds * 1000);
  }, [liveQuery.data]);

  useEffect(() => {
    const freshRaw = liveQuery.data?.data?.items;
    if (freshRaw === undefined) return;
    const fresh = freshRaw.map(toMessageRow);

    isAtLatestBeforeUpdateRef.current = scroll.isAtLatest;

    const run = async (): Promise<void> => {
      if (messages.length === 0) {
        setMessages(fresh);
        setOlderCursor(liveQuery.data?.data?.nextCursor ?? null);
        setHasMoreOlder((liveQuery.data?.data?.nextCursor ?? null) !== null);
        // SOCIAL-NEW-1. A conversation opens on its newest message. A deep
        // link to a pinned or quoted message scrolls to that one instead.
        if (fresh.length > 0 && !props.jumpToMessageId) {
          scroll.stickToLatest();
        }
        return;
      }

      if (hasCatchUpGap({ existing: messages, freshFirstPage: fresh })) {
        try {
          const merged = await catchUpMessages({
            existing: messages,
            freshFirstPage: fresh,
            freshNextCursor: liveQuery.data?.data?.nextCursor ?? null,
            fetchPage: async (cursor) => {
              const r = await callSdk().postMessageList({ chatId, cursor });
              return {
                items: (r.data?.items ?? []).map(toMessageRow),
                nextCursor: r.data?.nextCursor ?? null,
              };
            },
          });
          setMessages(merged);
          scroll.onNewMessagesArrived(isAtLatestBeforeUpdateRef.current);
          return;
        } catch {
          // A failed catch-up degrades to "merge what we have."
        }
      }

      const merged = mergeFreshPage({
        existing: messages,
        freshFirstPage: fresh,
      });
      setMessages(merged);
      scroll.onNewMessagesArrived(isAtLatestBeforeUpdateRef.current);
    };

    void run();
  }, [liveQuery.data?.data?.items]);

  useEffect(() => {
    const step = resolveMarkReadStep({
      chatId,
      isFocused: true,
      newestMessageId: liveQuery.data?.data?.items[0]?.messageId ?? null,
      lastSent: lastMarkReadRef.current,
    });
    if (!step.doSend) return;
    lastMarkReadRef.current = step.nextLastSent;
    markRead.mutate({ chatId });
  }, [chatId, liveQuery.data]);

  const loadOlder = async (): Promise<void> => {
    if (isLoadingOlder || !hasMoreOlder || olderCursor === null) return;
    setIsLoadingOlder(true);
    setIsPrependingOlder(true);
    try {
      const response = await callSdk().postMessageList({
        chatId,
        cursor: olderCursor,
      });
      if (response.error || !response.data) {
        showApiError(response);
        return;
      }
      const olderPage = response.data.items.map(toMessageRow);
      setMessages((prev) => appendOlderPage({ existing: prev, olderPage }));
      setOlderCursor(response.data.nextCursor ?? null);
      setHasMoreOlder((response.data.nextCursor ?? null) !== null);
    } catch {
      showOfflineError();
    } finally {
      setIsLoadingOlder(false);
    }
  };

  // Runs after `useConversationScroll`'s layout effect has consumed
  // `isPrependingOlder` for this commit (layout effects flush before
  // passive effects), so this only ever clears a flag already acted on.
  useEffect(() => {
    if (isPrependingOlder) setIsPrependingOlder(false);
  }, [rowCount]);

  const onScroll = (): void => {
    scroll.onScroll();
    const container = containerRef.current;
    if (container && container.scrollTop <= LOAD_OLDER_THRESHOLD_PX) {
      void loadOlder();
    }
  };

  /**
   * ISSUE-1054: jump to a pinned message. A loaded row scrolls and
   * highlights as before. An unloaded one walks back through older pages,
   * bounded by `JUMP_BACK_WALK_MAX_PAGES`, and a miss shows an inline note
   * without moving the scroll position (walked rows are only kept on a hit).
   */
  const handleJumpToMessage = async (messageId: string): Promise<void> => {
    jumpWalkTokenRef.current += 1;
    const token = jumpWalkTokenRef.current;
    setJumpNote(null);

    if (scroll.jumpToMessage(messageId)) return;

    // Loaded but with no bubble: deleted, or a collapsed blocked sender.
    if (messages.some((m) => m.messageId === messageId)) {
      setJumpNote("notFound");
      return;
    }
    if (!hasMoreOlder || olderCursor === null) {
      setJumpNote("notFound");
      return;
    }

    setJumpNote("searching");
    activeWalkTokenRef.current = token;
    setIsLoadingOlder(true);
    let result: typeJumpWalkResult<typeMessageRow>;
    try {
      result = await walkBackToMessage<typeMessageRow>({
        messageId,
        startCursor: olderCursor,
        maxPages: JUMP_BACK_WALK_MAX_PAGES,
        fetchPage: async (cursor) => {
          const r = await callSdk().postMessageList({
            chatId,
            cursor,
            limit: JUMP_BACK_WALK_PAGE_LIMIT,
          });
          if (r.error || !r.data) {
            showApiError(r);
            return null;
          }
          return {
            items: r.data.items.map(toMessageRow),
            nextCursor: r.data.nextCursor ?? null,
          };
        },
      });
    } catch {
      if (token === jumpWalkTokenRef.current) {
        setJumpNote(null);
        showOfflineError();
      }
      return;
    } finally {
      if (activeWalkTokenRef.current === token) {
        activeWalkTokenRef.current = null;
        setIsLoadingOlder(false);
      }
    }

    // A newer jump or a chat change superseded this one.
    if (token !== jumpWalkTokenRef.current) return;

    switch (result.status) {
      case "found":
        setMessages((prev) =>
          appendOlderPage({ existing: prev, olderPage: result.olderRows }),
        );
        setOlderCursor(result.nextCursor);
        setHasMoreOlder(result.nextCursor !== null);
        pendingJumpMessageIdRef.current = messageId;
        setJumpNote(null);
        return;
      case "capReached":
        setJumpNote("tooFarBack");
        return;
      case "exhausted":
        setJumpNote("notFound");
        return;
      case "failed":
        // The shared error modal is already up.
        setJumpNote(null);
        return;
    }
  };

  // Runs the jump once a found walk's rows are in the DOM.
  useEffect(() => {
    const pending = pendingJumpMessageIdRef.current;
    if (pending === null) return;
    pendingJumpMessageIdRef.current = null;
    if (!scroll.jumpToMessage(pending)) setJumpNote("notFound");
  }, [rowCount]);

  // Drops any walk in flight and its note when the chat changes. Declared
  // before the deep-link effect so a chat change that also carries a
  // `messageId` starts its jump after this reset, not before it.
  useEffect(() => {
    jumpWalkTokenRef.current += 1;
    pendingJumpMessageIdRef.current = null;
    setJumpNote(null);
  }, [chatId]);

  // RF-3: the panel closed itself (it went through `closeOverlay` as part of
  // setting this same `messageId`), so this effect only ever needs to move
  // the scroll position, never the overlay state. It waits for the first
  // page, so a cold deep link does not report a false miss.
  const hasRows = rowCount > 0;
  useEffect(() => {
    if (!props.jumpToMessageId || !hasRows) return;
    void handleJumpToMessage(props.jumpToMessageId);
  }, [props.jumpToMessageId, hasRows]);

  const findRawMessage = (messageId: string): typeMessageRow | undefined =>
    messages.find((m) => m.messageId === messageId);

  /**
   * The one network call every send and every retry funnels through,
   * regardless of kind: mark the pseudo-row sending, call `/message/send`
   * with whatever `buildSendVariables` built for this payload's kind, then
   * either swap in the server's real `messageId`/`sentAtIso` or mark the row
   * failed. `handleSend`/`handleSendPhotos`/`handleSendPoll`/`handleSendPdf`
   * differ only in
   * the pseudo-row they build before calling this; `retrySend` skips
   * straight here with the payload it already has on file.
   */
  const submitPending = async (
    pseudoId: string,
    payload: typePendingSendPayload,
  ): Promise<void> => {
    setPendingSendState((prev) => ({ ...prev, [pseudoId]: "sending" }));
    try {
      const result = await messageSend.mutateAsync(
        buildSendVariables(chatId, payload),
      );
      if (result.error || !result.data) {
        setPendingSendState((prev) => ({ ...prev, [pseudoId]: "failed" }));
        // ISSUE-1368. Staff switched chat off while this screen was open.
        // Re-read the chat so the composer gives way to the line saying so.
        if (result.error?.code === "CHAT_SUSPENDED") {
          void chatGet.refetch();
        }
        return;
      }
      const data = result.data;
      setMessages((prev) => {
        const swapped = prev.map((m) =>
          m.messageId === pseudoId
            ? { ...m, messageId: data.messageId, sentAtIso: data.sentAtIso }
            : m,
        );
        // Drop any copy of this message the poll already delivered
        // (ISSUE-842). `/message/send` is idempotent on `requestId`, so a send
        // that reached the server but whose response was lost comes back with
        // the id the poll has ALREADY put on screen. Without this the swap
        // produces two rows sharing one messageId, which is both a visible
        // duplicate and a duplicate React key in the row list.
        const seen = new Set<string>();
        return swapped.filter((m) => {
          if (seen.has(m.messageId)) return false;
          seen.add(m.messageId);
          return true;
        });
      });
      setPendingSendState((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([key]) => key !== pseudoId),
        ),
      );
      // Cleared alongside the send state. Leaving it keyed by a pseudo-id no
      // row carries any more leaked one entry per send for the life of the
      // mounted pane.
      setPendingSendPayload((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([key]) => key !== pseudoId),
        ),
      );
    } catch {
      setPendingSendState((prev) => ({ ...prev, [pseudoId]: "failed" }));
    }
  };

  const stageAndSubmit = (
    pseudoRow: typeMessageRow,
    payload: typePendingSendPayload,
  ): void => {
    setMessages((prev) => [pseudoRow, ...prev]);
    // SOCIAL-NEW-1. Your own message always lands in view, even if you had
    // scrolled up to read history before sending it.
    scroll.stickToLatest();
    setPendingSendPayload((prev) => ({
      ...prev,
      [pseudoRow.messageId]: payload,
    }));
    setReplyTo(null);
    void submitPending(pseudoRow.messageId, payload);
  };

  const basePseudoRow = (
    requestId: string,
    replyToMessageId: string | null,
  ): Omit<typeMessageRow, "kind" | "body" | "mediaObjectIds" | "poll"> & {
    messageId: string;
  } => ({
    // Built by the merge module, which is the thing that has to recognise the
    // prefix later. Interpolating it here a second time is how the two drifted
    // apart and the merge stopped knowing its own convention (ISSUE-842).
    messageId: optimisticMessageId(requestId),
    senderId: viewerUserId,
    senderDisplayName: "",
    senderAvatarUrl: null,
    gameCard: null,
    pdf: null,
    gifObjectId: null,
    replyToMessageId,
    replyToPreview: replyTo?.preview ?? null,
    isEdited: false,
    isPinned: false,
    isDeleted: false,
    sentAtIso: new Date().toISOString(),
    editedAtIso: null,
    announcementKind: null,
    reactions: [],
    linkPreview: null,
  });

  const handleSend = (body: string, replyToMessageId: string | null): void => {
    const requestId = crypto.randomUUID();
    const base = basePseudoRow(requestId, replyToMessageId);
    stageAndSubmit(
      { ...base, kind: "text", body, mediaObjectIds: null, poll: null },
      { kind: "text", requestId, body, replyToMessageId },
    );
  };

  const handleSendPhotos = (
    mediaObjectIds: string[],
    caption: string | null,
    replyToMessageId: string | null,
  ): void => {
    const requestId = crypto.randomUUID();
    const base = basePseudoRow(requestId, replyToMessageId);
    stageAndSubmit(
      { ...base, kind: "image", body: caption, mediaObjectIds, poll: null },
      {
        kind: "image",
        requestId,
        mediaObjectIds,
        body: caption,
        replyToMessageId,
      },
    );
  };

  const handleSendPoll = (
    pollDraft: {
      question: string;
      allowMultiple: boolean;
      optionLabels: string[];
    },
    replyToMessageId: string | null,
  ): void => {
    const requestId = crypto.randomUUID();
    const base = basePseudoRow(requestId, replyToMessageId);
    // A locally-fabricated poll shape so the fan's own poll renders
    // (before-voting) the instant it's sent. `optionId` values here are not
    // real -- the next `/message/list` refetch replaces this whole pseudo-row
    // by `messageId` (`mergeFreshPage`'s "fresh page always wins" rule) once
    // the server assigns real ones, exactly as it does for a real `sentAtIso`.
    stageAndSubmit(
      {
        ...base,
        kind: "poll",
        body: null,
        mediaObjectIds: null,
        poll: {
          question: pollDraft.question,
          allowMultiple: pollDraft.allowMultiple,
          options: pollDraft.optionLabels.map((label, index) => ({
            optionId: `pending-option-${index}`,
            label,
            voteCount: 0,
          })),
          myVoteOptionIds: [],
          totalVotes: 0,
        },
      },
      { kind: "poll", requestId, pollDraft, replyToMessageId },
    );
  };

  /**
   * ISSUE-1057 R4. The PDF is already uploaded and published, so this is the
   * same optimistic row and retry path as a photo. `mediaObjectIds` carries
   * the one id because `MessageBubble` reads `mediaObjectIds[0]` for a pdf
   * row.
   */
  const handleSendPdf = (
    pdf: typeComposerPdfSend,
    replyToMessageId: string | null,
  ): void => {
    const requestId = crypto.randomUUID();
    const base = basePseudoRow(requestId, replyToMessageId);
    stageAndSubmit(
      {
        ...base,
        kind: "pdf",
        body: null,
        mediaObjectIds: [pdf.mediaObjectId],
        poll: null,
        pdf: {
          fileName:
            pdf.fileName.length > 0 ? pdf.fileName : PDF_FILE_NAME_FALLBACK,
          byteSize: pdf.byteSize,
        },
      },
      {
        kind: "pdf",
        requestId,
        mediaObjectId: pdf.mediaObjectId,
        replyToMessageId,
      },
    );
  };

  /**
   * R3. A tapped GIF tile is two legs, and only the second one is a message.
   *
   * `/gif/select` re-hosts the provider's bytes as one of our own media
   * objects (Q201) and hands back a `mediaId`; only then can `/message/send`
   * carry a `gifObjectId`. There is no optimistic row before the select
   * resolves, because until it does there is nothing to draw: the bubble
   * renders the object, and the object does not exist yet.
   *
   * A refused select surfaces through the `gifSelect` error context, which
   * is what turns media-api's GIF_RATING_NOT_ALLOWED into a sentence about
   * the rating rather than a generic failure (ISSUE-778 D3). This is the
   * first screen on the platform that actually calls the endpoint, so it is
   * the first that can raise that copy.
   */
  const handleSendGif = async (
    providerGifId: string,
    replyToMessageId: string | null,
  ): Promise<void> => {
    const requestId = crypto.randomUUID();
    let result;
    try {
      result = await gifSelect.mutateAsync({ providerGifId, requestId });
    } catch {
      showOfflineError();
      return;
    }
    if (showApiError(result, "gifSelect")) return;
    const gifObjectId = result.data?.mediaId;
    if (!gifObjectId) return;
    const base = basePseudoRow(requestId, replyToMessageId);
    stageAndSubmit(
      {
        ...base,
        kind: "gif",
        body: null,
        mediaObjectIds: null,
        poll: null,
        gifObjectId,
      },
      { kind: "gif", requestId, gifObjectId, replyToMessageId },
    );
  };

  const retrySend = (pseudoId: string): void => {
    const payload = pendingSendPayload[pseudoId];
    if (!payload) return;
    void submitPending(pseudoId, payload);
  };

  /**
   * R7: voting needs no confirm, so this fires the mutation directly on tap.
   * `usePollVote`'s own `onSuccess` already invalidates `message:list`
   * (`usePollVote.ts`, 150ms debounced), so the poll's bars and percentages
   * catch up on the next refetch; this handler only tracks the in-flight
   * and failed states `PollBubble` needs to stay distinguishable from its
   * settled after-voting look.
   */
  const handleVote = (messageId: string, optionIds: string[]): void => {
    setVotingMessageIds((prev) => ({ ...prev, [messageId]: true }));
    setFailedVoteMessageIds((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([key]) => key !== messageId),
      ),
    );
    pollVote.mutate(
      { messageId, optionIds },
      {
        onSuccess: (result) => {
          setVotingMessageIds((prev) =>
            Object.fromEntries(
              Object.entries(prev).filter(([key]) => key !== messageId),
            ),
          );
          if (result.error || !result.data) {
            setFailedVoteMessageIds((prev) => ({ ...prev, [messageId]: true }));
          }
        },
        onError: () => {
          setVotingMessageIds((prev) =>
            Object.fromEntries(
              Object.entries(prev).filter(([key]) => key !== messageId),
            ),
          );
          setFailedVoteMessageIds((prev) => ({ ...prev, [messageId]: true }));
        },
      },
    );
  };

  const handleSaveEdit = async (
    messageId: string,
    body: string,
  ): Promise<void> => {
    try {
      const result = await messageEdit.mutateAsync({ messageId, body });
      if (result.error || !result.data) {
        showApiError(result);
        return;
      }
      const editedAtIso = result.data.editedAtIso;
      setMessages((prev) =>
        prev.map((m) =>
          m.messageId === messageId
            ? { ...m, body, isEdited: true, editedAtIso }
            : m,
        ),
      );
      setEditingMessage(null);
    } catch {
      showOfflineError();
    }
  };

  const buildActionRows = (
    target: Extract<typeRenderRow, { kind: "message" }>,
  ): typeActionMenuRow[] => {
    const raw = findRawMessage(target.messageId);
    const copyText = resolveCopyText({
      kind: target.messageKind,
      body: raw?.body ?? null,
    });
    const rowsBuilt: typeActionMenuRow[] = [
      {
        key: "reply",
        label: "Reply",
        onSelect: () => {
          setReplyTo({
            messageId: target.messageId,
            preview: stripMentionMarkup(raw?.body ?? ""),
          });
        },
      },
      {
        key: "react",
        label: "React",
        onSelect: () => {
          setReactionTargetId(target.messageId);
        },
      },
    ];

    // ISSUE-1048 R1: Copy only where the bubble shows text to copy.
    if (copyText !== null) {
      rowsBuilt.push({
        key: "copy",
        label: "Copy",
        onSelect: () => {
          void navigator.clipboard.writeText(copyText);
        },
      });
    }

    // ISSUE-1048 R2/R4: Edit only on your own text, or your own captioned photo.
    // ISSUE-1368 and ISSUE-1364: and not while staff have chat switched off for
    // the account or an officer has the fan timed out here.
    if (
      canEditMessage({
        kind: target.messageKind,
        body: raw?.body ?? null,
        isOwn: target.isOwn,
        isPostingClosed:
          chatGet.data?.data?.isChatSuspended === true ||
          activeTimeoutIso(rawTimedOutUntilIso, new Date()) !== null,
      })
    ) {
      rowsBuilt.push({
        key: "edit",
        label: "Edit",
        onSelect: () => {
          setEditingMessage({
            messageId: target.messageId,
            body: raw?.body ?? "",
          });
        },
      });
    }

    // Widened from "own message only" (ISSUE-660 WI-W10): `canDeleteMessage`
    // is the same server-mirroring gate `Pin` already reads off `canModerate`
    // below, so a moderator now sees `Delete` on another member's message
    // too. This REPLACES the own-message-only entry the block above used to
    // push; a fan never sees `Delete` twice.
    if (
      canDeleteMessage({
        senderId: target.senderId,
        callerId: viewerUserId,
        canModerate: chatGet.data?.data?.canModerate === true,
      })
    ) {
      rowsBuilt.push({
        key: "delete",
        label: DELETE_ACTION_LABEL,
        isDestructive: true,
        onSelect: () => {
          void (async () => {
            const result = await deleteMessage({
              messageId: target.messageId,
              senderId: target.senderId,
              callerId: viewerUserId,
              senderDisplayName: target.senderDisplayName,
            });
            if (result.didDelete) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.messageId === target.messageId
                    ? { ...m, isDeleted: true, body: null }
                    : m,
                ),
              );
            }
          })();
        },
      });
    }

    // Report is on every message that is not the fan's own, like Block. It
    // used to sit on the fan's own messages too (ISSUE-660 WI-W10), where it
    // could only report yourself (QA round 1). Opens the report dialog
    // `messages.tsx` already mounts (ISSUE-271 WI-W8/WI-W9), through the
    // same `overlay` this pane uses to open the chat details sheet, so the
    // open is one history entry.
    if (!target.isOwn) {
      rowsBuilt.push({
        key: "report",
        label: REPORT_ACTION_LABEL,
        onSelect: () => {
          overlay.open({
            kind: "dialog",
            name: REPORT_DIALOG,
            params: { messageId: target.messageId, senderId: target.senderId },
          });
        },
      });
    }

    // Block is on every message that is not the fan's own.
    if (!target.isOwn) {
      rowsBuilt.push({
        key: "block",
        label: BLOCK_ACTION_LABEL,
        isDestructive: true,
        onSelect: () => {
          void blockFan({
            accountId: target.senderId,
            displayName: target.senderDisplayName,
          });
        },
      });
    }

    // RF-1: pushed immediately before this return, after the rows above, so
    // it lands after Reply/React/Copy/Edit/Delete on a fan's own message
    // and after Reply/React/Copy/Delete(if moderator)/Report/Block on
    // anyone else's.
    if (chatGet.data?.data?.canModerate === true) {
      const isPinned = raw?.isPinned ?? false;
      rowsBuilt.push({
        key: "pin",
        label: isPinned ? "Unpin" : "Pin",
        onSelect: () => {
          void (async () => {
            try {
              const mutation = isPinned ? messageUnpin : messagePin;
              const result = await mutation.mutateAsync({
                messageId: target.messageId,
              });
              if (
                (result.error ?? null) !== null ||
                (result.data ?? null) === null
              ) {
                showApiError(result);
              }
            } catch {
              showOfflineError();
            }
          })();
        },
      });
    }

    return rowsBuilt;
  };

  const reactorsTargetRow =
    reactorsTarget === null
      ? undefined
      : rows.find(
          (row) =>
            row.kind === "message" &&
            row.messageId === reactorsTarget.messageId,
        );
  const reactorsTargetReactions =
    reactorsTargetRow?.kind === "message" ? reactorsTargetRow.reactions : [];

  const handleReactionSelect = async (emoji: string): Promise<void> => {
    if (reactionTargetId === null) return;
    const target = rows.find(
      (row) => row.kind === "message" && row.messageId === reactionTargetId,
    );
    const currentReaction =
      target?.kind === "message"
        ? (target.reactions.find((r) => r.reactedByMe)?.emoji ?? null)
        : null;
    const nextEmoji = resolveReactionTap({
      currentReaction,
      tappedEmoji: emoji,
    });
    try {
      await reactionSet.mutateAsync({
        messageId: reactionTargetId,
        emoji: nextEmoji,
      });
    } catch {
      showOfflineError();
    }
    setReactionTargetId(null);
  };

  const members = (chatMembers.data?.data?.items ?? []).map((m) => ({
    userId: m.userId,
    displayName: m.displayName,
  }));

  const isEnabled = chatGet.data?.data?.isEnabled;
  const canPost = chatGet.data?.data?.canPost;
  const timedOutUntilIso = activeTimeoutIso(
    chatGet.data?.data?.timedOutUntilIso,
    new Date(),
  );
  const chatType = chatGet.data?.data?.chatType;
  const isEmpty =
    messages.length === 0 && liveQuery.data?.data?.items.length === 0;

  let composerOrGatedLine: ReactNode;
  if (isEnabled === false) {
    composerOrGatedLine = (
      <p className="text-caption text-ink-tertiary">{chatOffLine(chatType)}</p>
    );
  } else if (chatGet.data?.data?.isChatSuspended === true) {
    // ISSUE-1368. Staff switched chat off for this whole account. Reads
    // before a timeout: it covers every chat and has no end time.
    composerOrGatedLine = (
      <p
        className="text-caption text-ink-tertiary"
        data-testid="conversation-chat-suspended"
      >
        {CHAT_SUSPENDED_COMPOSER_LINE}
      </p>
    );
  } else if (timedOutUntilIso !== null) {
    // ISSUE-1364. An officer's timeout reads before announcements-only: it is
    // about this fan, and it says until when.
    composerOrGatedLine = (
      <p
        className="text-caption text-ink-tertiary"
        data-testid="conversation-timed-out"
      >
        {timedOutComposerLine({
          untilIso: timedOutUntilIso,
          reason: chatGet.data?.data?.timedOutReason ?? null,
          now: new Date(),
        })}
      </p>
    );
  } else if (canPost === false) {
    composerOrGatedLine = (
      <p className="text-caption text-ink-tertiary">
        {announcementsOnlyLine(chatType)}
      </p>
    );
  } else {
    composerOrGatedLine = (
      <Composer
        chatId={chatId}
        members={members}
        replyTo={replyTo}
        onCancelReply={() => {
          setReplyTo(null);
        }}
        editingMessage={editingMessage}
        onCancelEdit={() => {
          setEditingMessage(null);
        }}
        onSend={(body, replyToMessageId) => {
          handleSend(body, replyToMessageId);
        }}
        onSendPhotos={(mediaObjectIds, caption, replyToMessageId) => {
          handleSendPhotos(mediaObjectIds, caption, replyToMessageId);
        }}
        onSendPoll={(pollDraft, replyToMessageId) => {
          handleSendPoll(pollDraft, replyToMessageId);
        }}
        onSendGif={(providerGifId, replyToMessageId) => {
          void handleSendGif(providerGifId, replyToMessageId);
        }}
        onSendPdf={(pdf, replyToMessageId) => {
          handleSendPdf(pdf, replyToMessageId);
        }}
        onSaveEdit={(messageId, body) => void handleSaveEdit(messageId, body)}
        photoIntakeRef={photoIntakeRef}
        isSubmitting={
          messageSend.isPending || messageEdit.isPending || gifSelect.isPending
        }
      />
    );
  }

  const muteHeaderText = muteHeaderLabel(
    chatGet.data?.data?.muteState ?? "off",
    chatGet.data?.data?.mutedUntil ?? null,
    new Date(),
  );

  // ISSUE-1366. Image files dropped anywhere on the pane go to the composer's
  // photo tray, the same as picking them through Attach. A depth counter
  // rather than a flag, because dragenter and dragleave fire for every child
  // the pointer crosses on its way over the pane.
  const handleDragEnter = (event: DragEvent<HTMLDivElement>): void => {
    if (!isFileDrag(event.dataTransfer.types)) return;
    if (photoIntakeRef.current === null) return;
    event.preventDefault();
    dragDepthRef.current += 1;
    setIsDraggingFiles(true);
  };
  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    if (!isFileDrag(event.dataTransfer.types)) return;
    if (photoIntakeRef.current === null) return;
    // Without this the browser opens the dropped file in the tab instead.
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
  };
  const handleDragLeave = (event: DragEvent<HTMLDivElement>): void => {
    if (!isFileDrag(event.dataTransfer.types)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDraggingFiles(false);
  };
  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    dragDepthRef.current = 0;
    setIsDraggingFiles(false);
    const intake = photoIntakeRef.current;
    if (intake === null) return;
    const images = imageFilesFrom(Array.from(event.dataTransfer.files));
    if (images.length === 0) return;
    event.preventDefault();
    intake(images);
  };

  return (
    <div
      className="sm-conversation-pane sm-docked-action relative flex h-[calc(100dvh-11.5rem)] flex-col"
      data-testid="conversation-pane"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Always mounted so it fades in and out (ui-motion.mdc) instead of
          popping; it never takes a pointer, so the drop lands on the pane. */}
      <div
        data-testid="conversation-drop-overlay"
        data-visible={isDraggingFiles ? "true" : "false"}
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md border-2 border-dashed border-primary bg-primary-tint transition-opacity duration-200 ${
          isDraggingFiles ? "opacity-100" : "opacity-0"
        } ${REDUCED_MOTION_SAFE}`}
      >
        <span className="text-body-strong text-primary">
          {COMPOSER_DROP_PHOTOS_LABEL}
        </span>
      </div>
      {/*
       * ISSUE-281 makes the whole header a tap target that opens the details
       * sheet; ISSUE-283 adds a Members button for a standalone chat. The
       * button is a sibling of the header rather than a child of it, because
       * a button nested inside a button is invalid HTML and the inner click
       * never arrives. The row owns the border so the header keeps the full
       * width rule it had before the Members button joined it.
       */}
      <div className="flex shrink-0 items-center gap-2 border-b border-hairline">
        {props.onBack !== undefined ? (
          <Button
            variant="ghost"
            size="icon"
            aria-label={CONVERSATION_BACK_LABEL}
            data-testid="conversation-back"
            className="-ml-2 shrink-0 lg:hidden"
            onClick={props.onBack}
          >
            <ConversationBackGlyph />
          </Button>
        ) : null}
        <button
          type="button"
          aria-haspopup="dialog"
          data-testid="conversation-header"
          className="flex min-w-0 flex-1 items-center gap-3 px-0 py-3 text-left"
          onClick={() => {
            overlay.open({ kind: "sheet", name: CHAT_DETAILS_SHEET });
          }}
        >
          {chatGet.data?.data !== undefined && chatGet.data.data !== null ? (
            <ChatAvatar
              title={chatGet.data.data.title}
              photoUrl={headerPhotoUrl}
              testId="conversation-header-avatar"
            />
          ) : null}
          <span className="flex min-w-0 flex-1 flex-col items-start gap-1">
            <span className="text-body-strong text-ink">
              {chatGet.data?.data?.title ?? "Chat"}
            </span>
            {muteHeaderText !== null ? (
              <span
                data-testid="conversation-header-muted"
                className="text-caption text-ink-secondary"
              >
                {muteHeaderText}
              </span>
            ) : null}
          </span>
        </button>
        {chatType === "standalone" ? (
          <Button
            variant="secondary"
            size="sm"
            data-testid="conversation-members"
            onClick={() => {
              setIsMembersOpen(true);
            }}
          >
            {CHAT_MEMBERS_TITLE}
          </Button>
        ) : null}
      </div>
      <PinnedBar
        chatId={chatId}
        onJumpToMessage={(messageId) => {
          void handleJumpToMessage(messageId);
        }}
      />
      {jumpNote !== null ? (
        <div
          data-testid="jump-note"
          role="status"
          className="flex items-center gap-2 border-b border-hairline bg-surface-secondary px-4 py-2"
        >
          <span className="flex-1 text-caption text-ink-secondary">
            {JUMP_NOTE_COPY[jumpNote]}
          </span>
          {jumpNote !== "searching" ? (
            <button
              type="button"
              data-testid="jump-note-dismiss"
              className="min-h-touch-target shrink-0 text-caption text-primary"
              onClick={() => {
                setJumpNote(null);
              }}
            >
              {JUMP_NOTE_DISMISS_LABEL}
            </button>
          ) : null}
        </div>
      ) : null}
      <div
        ref={containerRef}
        className="sm-conversation-list flex-1 overflow-y-auto"
        onScroll={onScroll}
      >
        {isEmpty ? (
          <SectionEmptyState
            headline="No messages yet"
            body={CHAT_EMPTY_STATE_BODY}
          />
        ) : (
          orderedRows.map((row) => {
            if (row.kind === "day-divider")
              return <DayDivider key={row.key} label={row.label} />;
            if (row.kind === "season-divider")
              return <SeasonDivider key={row.key} label={row.label} />;
            if (row.kind === "system-line")
              return <SystemLine key={row.key} text={row.text} />;
            if (row.kind === "deleted")
              return <SystemLine key={row.key} text="Message deleted" />;
            const sendState = pendingSendState[row.messageId];
            const bubbleProps = {
              row,
              ...(sendState !== undefined ? { sendState } : {}),
              onRetry: () => {
                retrySend(row.messageId);
              },
              onSelectAction: () => {
                setActionTarget(row);
              },
              onOpenReactors: (emoji: string) => {
                setReactorsTarget({ messageId: row.messageId, emoji });
              },
              // ISSUE-1356. Same `/fan?accountId=` route MembersTab links to;
              // `senderId` is already in account-id space (ISSUE-271).
              onOpenSenderProfile: () => {
                void navigate({
                  to: "/fan",
                  search: { accountId: row.senderId },
                });
              },
              isVoting: votingMessageIds[row.messageId] === true,
              voteFailed: failedVoteMessageIds[row.messageId] === true,
              onVote: (optionIds: string[]) => {
                handleVote(row.messageId, optionIds);
              },
            };
            // WI-W12: a blocked sender's message collapses behind the
            // already-shipped `BlockedMessageLine` (ISSUE-271 WI-W6) rather
            // than being filtered from the list. `isSenderBlockedByRoster`
            // reads `senderId`, never `senderDisplayName` (moderationRules.ts's
            // header explains why the display name cannot be used here).
            if (
              isSenderBlockedByRoster({
                senderId: row.senderId,
                isOwnMessage: row.isOwn,
                roster: blockedRoster,
              })
            ) {
              return (
                <BlockedMessageLine key={row.key}>
                  <MessageBubble {...bubbleProps} />
                </BlockedMessageLine>
              );
            }
            return <MessageBubble key={row.key} {...bubbleProps} />;
          })
        )}
      </div>
      {scroll.newMessagesBelowCount > 0 ? (
        <button
          type="button"
          onClick={scroll.scrollToLatest}
          className="self-center rounded-full bg-primary-solid px-3 py-1 text-caption text-on-primary"
        >
          {scroll.newMessagesBelowCount} new message
          {scroll.newMessagesBelowCount === 1 ? "" : "s"}
        </button>
      ) : null}
      <div className="shrink-0 border-t border-hairline py-3">
        {composerOrGatedLine}
      </div>
      <MessageActionDialog
        open={actionTarget !== null}
        rows={actionTarget !== null ? buildActionRows(actionTarget) : []}
        onOpenChange={(open) => {
          if (!open) setActionTarget(null);
        }}
      />
      <ReactionPickerDialog
        open={reactionTargetId !== null}
        onSelect={(emoji) => void handleReactionSelect(emoji)}
        onOpenChange={(open) => {
          if (!open) setReactionTargetId(null);
        }}
      />
      <ReactorsDialog
        open={reactorsTarget !== null}
        onOpenChange={(open) => {
          if (!open) setReactorsTarget(null);
        }}
        messageId={reactorsTarget?.messageId ?? null}
        initialEmoji={reactorsTarget?.emoji ?? null}
        reactions={reactorsTargetReactions}
      />
      <ChatMembersDialog
        open={isMembersOpen}
        onOpenChange={setIsMembersOpen}
        chatId={chatId}
        onLeftOrDeleted={() => {
          onLeftOrDeleted?.();
        }}
      />
    </div>
  );
};
